"use client";

import { useState, type ReactElement } from "react";
import { ContentSkeleton } from "@/components/site/content-skeleton";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Ellipsis, PenLine, Share2, Trash2 } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";

import { AdminDeleteConfirmDialog } from "@/components/admin/admin-delete-confirm-dialog";
import { AdminDialog, AdminDialogBody } from "@/components/admin/admin-dialog-layout";
import {
  DialogCancelButton,
  DialogConfirmButton,
  RadioOptionGroup,
  SettingsActionButton,
  SettingsSaveButton,
} from "@/components/admin/settings-form-controls";
import { buttonVariants } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import { DatetimeInput } from "@/components/ui/datetime-input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDateTime } from "@/lib/datetime/display";
import { DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HelpTip } from "@/components/ui/help-tip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-field";
import { apiFetch } from "@/lib/api-client";
import { SHARE_ACCESS, type ShareAccess } from "@/lib/ai/chat-ui";
import { cn } from "@/lib/utils";

export type ConversationAction =
  | { type: "rename"; id: string; title: string }
  | { type: "share"; id: string }
  | { type: "delete"; ids: string[]; title?: string };

export const CONVERSATION_MANAGER_KEY = ["member", "ai", "conversation-manager"] as const;

export function ConversationMenu({
  conversation,
  onAction,
  className,
}: {
  conversation: { id: string; title: string; shared?: boolean };
  onAction: (action: ConversationAction) => void;
  className?: string;
}) {
  const t = useTranslations("member.aiChat.history");
  const { id, title } = conversation;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        openOnHover
        delay={100}
        aria-label={t("more")}
        className={cn(
          "inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-popup-open:bg-muted",
          className,
        )}
      >
        <Ellipsis className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-24 min-w-0 space-y-0.5">
        <DropdownMenuItem className="py-2" onClick={() => onAction({ type: "rename", id, title })}>
          <PenLine />
          {t("rename")}
        </DropdownMenuItem>
        <DropdownMenuItem className="py-2" onClick={() => onAction({ type: "share", id })}>
          <Share2 />
          {t("share")}
          {conversation.shared ? (
            <span aria-label={t("shared")} className="ml-auto size-1.5 rounded-full bg-emerald-500" />
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="destructive"
          className="py-2"
          onClick={() => onAction({ type: "delete", ids: [id], title })}
        >
          <Trash2 />
          {t("delete")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ConversationActionDialogs({
  action,
  onClose,
  onChanged,
}: {
  action: ConversationAction | null;
  onClose: () => void;
  onChanged: (deletedIds: string[]) => void;
}) {
  const queryClient = useQueryClient();

  function changed(deletedIds: string[] = []) {
    void queryClient.invalidateQueries({ queryKey: CONVERSATION_MANAGER_KEY });
    onChanged(deletedIds);
  }

  return (
    <>
      <RenameDialog
        key={action?.type === "rename" ? action.id : "rename"}
        target={action?.type === "rename" ? action : null}
        onClose={onClose}
        onChanged={changed}
      />
      {action?.type === "share" ? (
        <ShareDialog key={action.id} id={action.id} onClose={onClose} onChanged={changed} />
      ) : null}
      <DeleteDialog
        target={action?.type === "delete" ? action : null}
        onClose={onClose}
        onChanged={changed}
      />
    </>
  );
}

function RenameDialog({
  target,
  onClose,
  onChanged,
}: {
  target: { id: string; title: string } | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useTranslations("member.aiChat.history");
  const [title, setTitle] = useState(target?.title ?? "");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!target) return;

    const next = title.trim();

    if (!next) {
      toast.error(t("titleRequired"));
      return;
    }

    setBusy(true);

    try {
      await apiFetch(`/api/member/ai/conversations/${target.id}`, {
        method: "PATCH",
        body: { title: next },
      });
      onClose();
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("renameFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminDialog
      open={Boolean(target)}
      onOpenChange={(next) => !next && onClose()}
      title={t("renameTitle")}
      size="sm"
      saving={busy}
      confirm={{ label: t("save"), onClick: () => void save() }}
    >
      <Input
        value={title}
        maxLength={60}
        autoFocus
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.nativeEvent.isComposing) {
            event.preventDefault();
            void save();
          }
        }}
        onChange={(event) => setTitle(event.target.value)}
      />
    </AdminDialog>
  );
}

type ShareState = {
  token: string;
  access: ShareAccess;
  expiresAt: string | null;
  maxViews: number;
  viewCount: number;
};

type ShareForm = {
  access: ShareAccess;
  expiresAt: string | null;
  maxViews: number | null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

function formOf(share: ShareState | null): ShareForm {
  return share
    ? { access: share.access, expiresAt: share.expiresAt, maxViews: share.maxViews }
    : {
        access: "login",

        expiresAt: new Date(Math.floor((Date.now() + DAY_MS) / 60_000) * 60_000).toISOString(),
        maxViews: 0,
      };
}

function ShareDialog({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useTranslations("member.aiChat.history");
  const ui = useTranslations("site.ui");
  const shareQuery = useQuery({
    queryKey: ["member", "ai", "conversation-share", id],
    queryFn: ({ signal }) =>
      apiFetch<{ share: ShareState | null }>(
        `/api/member/ai/conversations/${id}/share`,
        { signal },
      ).then((data) => data.share),
    gcTime: 0,
  });
  const ready = shareQuery.data !== undefined;

  return (
    <AdminDialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={t("shareTitle")}
      size="md"
      footer={ready ? undefined : <DialogCancelButton onClick={onClose}>{t("close")}</DialogCancelButton>}
      rawBody={ready}
    >
      {ready ? (
        <>
          {shareQuery.isError ? <div role="alert" className="px-4 pt-4 text-sm text-destructive">{shareQuery.error.message}<Button variant="link" onClick={() => void shareQuery.refetch()}>{ui("retry")}</Button></div> : null}
          <ShareForm id={id} initial={shareQuery.data!} onClose={onClose} onChanged={onChanged} />
        </>
      ) : (
        <div className="min-h-40 text-sm text-muted-foreground">
          {shareQuery.isError ? (
            <div role="alert">{shareQuery.error.message}<Button variant="link" onClick={() => void shareQuery.refetch()}>{ui("retry")}</Button></div>
          ) : (
            <ContentSkeleton variant="form" count={3} label={ui("loading")} />
          )}
        </div>
      )}
    </AdminDialog>
  );
}

function ShareForm({
  id,
  initial,
  onClose,
  onChanged,
}: {
  id: string;
  initial: ShareState | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useTranslations("member.aiChat.history");
  const [share, setShare] = useState(initial);
  const [form, setForm] = useState(() => formOf(initial));
  const [busy, setBusy] = useState(false);
  const url = share ? `${window.location.origin}/share/ai/${share.token}/` : "";
  const saved = formOf(share);
  const dirty =
    Boolean(share) &&
    (form.access !== saved.access ||
      form.expiresAt !== saved.expiresAt ||
      (form.maxViews ?? 0) !== saved.maxViews);

  async function save() {
    if (!form.expiresAt) {
      toast.error(t("expiresRequired"));
      return;
    }

    if (Date.parse(form.expiresAt) <= Date.now()) {
      toast.error(t("expiresPast"));
      return;
    }

    setBusy(true);

    try {
      const next = await apiFetch<{ share: ShareState }>(
        `/api/member/ai/conversations/${id}/share`,
        {
          method: "POST",
          body: { ...form, maxViews: form.maxViews ?? 0 },
        },
      );

      toast.success(share ? t("shareSaved") : t("linkGenerated"));
      setShare(next.share);
      setForm(formOf(next.share));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("shareFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function unshare() {
    setBusy(true);

    try {
      await apiFetch(`/api/member/ai/conversations/${id}/share`, { method: "DELETE" });
      toast.success(t("unshared"));
      onClose();
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("unshareFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <AdminDialogBody className="space-y-4">
        <div className="space-y-1.5">
          <Label>{t("access")}</Label>
          <RadioOptionGroup
            name={`share-access-${id}`}
            value={form.access}
            options={SHARE_ACCESS.map((value) => ({ value, label: t(`accessOption.${value}`) }))}
            onChange={(access) => setForm((current) => ({ ...current, access }))}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`share-expires-${id}`}>{t("expiresAt")}</Label>
          <DatetimeInput
            id={`share-expires-${id}`}
            value={form.expiresAt}
            onValueChange={(expiresAt) => setForm((current) => ({ ...current, expiresAt }))}
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-1">
            <Label htmlFor={`share-views-${id}`}>{t("maxViews")}</Label>
            <HelpTip>{t("maxViewsHint")}</HelpTip>
          </div>
          <NumberInput
            id={`share-views-${id}`}
            value={form.maxViews}
            min={0}
            integer
            onValueChange={(maxViews) => setForm((current) => ({ ...current, maxViews }))}
          />
          {share ? (
            <p className="text-xs text-muted-foreground">
              {t("viewCount", { count: share.viewCount })}
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-1">
            <Label htmlFor={`share-link-${id}`}>{t("link")}</Label>
            <HelpTip>{t("shareDescription")}</HelpTip>
          </div>
          <div className="flex items-center gap-2">
            <Input
              id={`share-link-${id}`}
              value={url}
              readOnly
              placeholder={t("linkPlaceholder")}
              className="bg-muted/40"
              onFocus={(event) => event.target.select()}
            />
            <CopyButton
              value={url}
              disabled={!url}
              label={t("copyLink")}
              successText={t("linkCopied")}
              className={buttonVariants({ variant: "outline", size: "icon" })}
            >
              {(copied) => (copied ? <Check className="text-emerald-500" /> : <Copy />)}
            </CopyButton>
          </div>
        </div>
      </AdminDialogBody>

      <DialogFooter>
        <DialogCancelButton onClick={onClose}>{t("close")}</DialogCancelButton>
        {share ? (
          <>
            <SettingsActionButton
              active={false}
              requireActive={false}
              hoverDestructive
              disabled={busy}
              onClick={() => void unshare()}
            >
              {t("unshare")}
            </SettingsActionButton>
            <SettingsSaveButton type="button" dirty={dirty} saving={busy} onClick={() => void save()}>
              {t("saveChanges")}
            </SettingsSaveButton>
          </>
        ) : (
          <DialogConfirmButton saving={busy} onClick={() => void save()}>
            {t("generate")}
          </DialogConfirmButton>
        )}
      </DialogFooter>
    </>
  );
}

function DeleteDialog({
  target,
  onClose,
  onChanged,
}: {
  target: { ids: string[]; title?: string } | null;
  onClose: () => void;
  onChanged: (deletedIds: string[]) => void;
}) {
  const t = useTranslations("member.aiChat.history");
  const [busy, setBusy] = useState(false);

  async function remove() {
    if (!target) return;

    setBusy(true);

    try {
      await apiFetch("/api/member/ai/conversations", {
        method: "DELETE",
        body: { ids: target.ids },
      });
      onClose();
      onChanged(target.ids);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("deleteFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminDeleteConfirmDialog
      open={Boolean(target)}
      onOpenChange={(next) => !next && onClose()}
      content={
        target?.ids.length === 1 && target.title
          ? t("deleteOne", { title: target.title })
          : t("deleteConfirm", { count: target?.ids.length ?? 0 })
      }
      saving={busy}
      onConfirm={() => void remove()}
    />
  );
}

export function ConversationTitleTip({
  conversation,
  trigger,
  side = "right",
  align = "center",
}: {
  conversation: { title: string; updatedAt: string; agent: { name: string } | null };

  trigger: ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
}) {
  const t = useTranslations("member.aiChat");
  const locale = useLocale();

  return (
    <Tooltip>
      <TooltipTrigger delay={400} render={trigger} />
      <TooltipContent side={side} align={align} className="flex-col items-start gap-0.5">
        <span className="opacity-70">{conversation.agent?.name ?? t("history.generalChat")}</span>
        <span className="break-all">{conversation.title || t("newChat")}</span>
        <span className="opacity-70">{formatDateTime(conversation.updatedAt, locale)}</span>
      </TooltipContent>
    </Tooltip>
  );
}
